import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js"
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import { useTranslation } from 'react-i18next';
import { SystemLabels } from '../_label/system/system_labels.js';
import { 
  hasSpecificRight
} from '../../assets/menus/menu_helper.js';

const Admin_Screen = () => {
    const { currentUser } = useContext(UserContext);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    // Get menu translation function
    const menuT = menu.getT();

    const setBreadCrumb = () => {
        var path = [{
            link: "/",
            name: t('home', { ns: 'labels' })
        },
        {
            link: "/admin",
            name: t('admin', { ns: 'labels' })
        }
        ];
        breadcrumb.set(path);
    }

    useEffect(() => {
        setBreadCrumb();
        menu.write({ user: currentUser() });
    }, []);

    // Define all admin menu items with their metadata
    const adminMenuItems = [
        {
            path: "/admin/users_and_groups",
            titleKey: "usersGroups.find",
            icon: "fa-users",
            color: "primary",
            requiredRight: "userAndGroupManagement",
            description: "Gestisci utenti e gruppi del sistema"
        },
        {
            path: "/admin/user",
            titleKey: "usersGroups.addUser",
            icon: "fa-user-plus",
            color: "success",
            requiredRight: "userAndGroupManagement",
            description: "Aggiungi un nuovo utente"
        },
        {
            path: "/admin/group",
            titleKey: "usersGroups.addGroup",
            icon: "fa-users-cog",
            color: "info",
            requiredRight: "userAndGroupManagement",
            description: "Crea un nuovo gruppo"
        },
        {
            path: "/admin/organization/business_units",
            titleKey: "organization.chart",
            icon: "fa-sitemap",
            color: "warning",
            requiredRight: "organizationChart",
            description: "Gestisci l'organigramma aziendale"
        },
        {
            path: "/admin/organization/functional_chart",
            titleKey: "organization.functional",
            icon: "fa-project-diagram",
            color: "secondary",
            requiredRight: "organizationChart",
            description: "Configura lo schema funzionale"
        },
        {
            path: "/admin/security/rights_tree",
            titleKey: "security.rightsTree",
            icon: "fa-shield-alt",
            color: "danger",
            requiredRight: "rightsTreeManagement",
            description: "Gestisci l'albero dei diritti"
        },
        {
            path: "/admin/vocs/organizational_chart_roles",
            titleKey: "vocs.orgRoles",
            icon: "fa-tags",
            color: "primary",
            requiredRight: "dictionary",
            description: "Vocabolario ruoli organizzativi"
        },
        {
            path: "/admin/vocs/functional_chart_roles",
            titleKey: "vocs.funcRoles",
            icon: "fa-tags",
            color: "success",
            requiredRight: "dictionary",
            description: "Vocabolario ruoli funzionali"
        },
        {
            path: "/admin/vocs/business_unit_types",
            titleKey: "vocs.unitTypes",
            icon: "fa-building",
            color: "info",
            requiredRight: "dictionary",
            description: "Tipi di unità aziendali"
        },
        {
            path: "/admin/vocs/hand_hygiene_oms_departments_category",
            titleKey: "vocs.vocDepartmentHandHygiene",
            icon: "fa-hand-sparkles",
            color: "warning",
            requiredRight: "dictionary",
            description: "Dipartimenti igiene delle mani"
        },
        {
            path: "/admin/vocs/hand_hygiene_oms_operators",
            titleKey: "vocs.vocOperatorsHandHygiene",
            icon: "fa-user-md",
            color: "secondary",
            requiredRight: "dictionary",
            description: "Operatori igiene delle mani"
        },
        {
            path: "/admin/vocs/risk_area",
            titleKey: "vocs.vocRisksArea",
            icon: "fa-exclamation-triangle",
            color: "danger",
            requiredRight: "dictionary",
            description: "Livelli di rischio"
        },
        {
            path: "/admin/vocs/form_groups",
            titleKey: "vocs.vocFormGroup",
            icon: "fa-layer-group",
            color: "primary",
            requiredRight: "dictionary",
            description: "Gruppi di form"
        },
        {
            path: "/admin/form_custom_vocs",
            titleKey: "vocs.formCustomVocs",
            icon: "fa-cogs",
            color: "success",
            requiredRight: "dictionary",
            description: "Vocabolari personalizzati"
        },
        {
            path: "/admin/vocs/hospital_regimen_types",
            titleKey: "vocs.hospitalRegimenTypes",
            icon: "fa-hospital",
            color: "info",
            requiredRight: "dictionary",
            description: "Regimi ospedalieri"
        },
        {
            path: "/form_builder",
            titleKey: "form.formBuilder",
            icon: "fa-wpforms",
            color: "warning",
            requiredRight: "formBuilder",
            description: "Creazione e modifica form"
        },
        {
            path: "/form_builder/finder",
            titleKey: "form.formBuilderFinder",
            icon: "fa-search",
            color: "secondary",
            requiredRight: "formAssign",
            description: "Ricerca form builder"
        },
        {
            path: "/form_builder/validation",
            titleKey: "form.validationDashboard",
            icon: "fa-check-double",
            color: "danger",
            requiredRight: "formValidate",
            description: "Dashboard validazione form"
        },
        {
            path: "/admin/configurations",
            titleKey: "configuration.manage",
            icon: "fa-cogs",
            color: "primary",
            requiredRight: "systemConfiguration",
            description: "Gestisci configurazioni di sistema"
        },
        {
            path: "/admin/data_import",
            titleKey: "dataImport.manage",
            icon: "fa-file-import",
            color: "success",
            requiredRight: "systemConfiguration",
            description: "Importa dati sanitari da file Excel"
        }
    ];

    // Filter items based on user rights
    const visibleItems = adminMenuItems.filter(item => {
        if (item.requiredRight) {
            return hasSpecificRight(currentUser(), item.requiredRight);
        }
        return true;
    });

    const handleItemClick = (path) => {
        navigate(path);
    };

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
            <header className="bg-white shadow-sm">
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    <ol className="breadcrumb mb-0"></ol>
                </div>
            </header>

            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid py-4">
                    <div className="row mb-4">
                        <div className="col-12">
                            <h2 className="text-center text-primary">
                                <i className="fa fa-users-cog me-2"></i>
                                {t('admin', { ns: 'labels' })}
                            </h2>
                            <p className="text-center text-muted">
                                {t('selectAdminFunction', { ns: 'labels' }) || 'Seleziona una funzione amministrativa'}
                            </p>
                        </div>
                    </div>

                    <div className="row g-4">
                        {visibleItems.map((item, index) => (
                            <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3">
                                <div 
                                    className={`card h-100 border-${item.color} shadow-sm hover-shadow`}
                                    style={{ 
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={() => handleItemClick(item.path)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-body text-center d-flex flex-column">
                                        <div className={`text-${item.color} mb-3`}>
                                            <i className={`fa ${item.icon} fa-3x`}></i>
                                        </div>
                                        <h5 className={`card-title text-${item.color} mb-2`}>
                                            {menuT(`menu.admin.${item.titleKey}`)}
                                        </h5>
                                        <p className="card-text text-muted small flex-grow-1">
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {visibleItems.length === 0 && (
                        <div className="row">
                            <div className="col-12">
                                <div className="text-center py-5">
                                    <i className="fa fa-exclamation-circle fa-3x text-muted mb-3"></i>
                                    <h4 className="text-muted">
                                        {t('noAdminFunctions', { ns: 'labels' }) || 'Nessuna funzione amministrativa disponibile'}
                                    </h4>
                                    <p className="text-muted">
                                        {t('contactAdmin', { ns: 'labels' }) || 'Contatta l\'amministratore per ottenere i permessi necessari'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <style jsx>{`
                .hover-shadow {
                    transition: box-shadow 0.3s ease;
                }
                
                .card:hover {
                    box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
                }
                
                .card-body:hover .fa {
                    transform: scale(1.1);
                    transition: transform 0.3s ease;
                }
                
                @media (max-width: 768px) {
                    .fa-3x {
                        font-size: 2rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export { Admin_Screen }; 